import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  getAuthClient,
  isBetterAuthEnabled,
  usePortalAuth,
} from 'utils/portalAuth'

import { LoginLayout } from './LoginLayout'
import { clearReturnTo, readReturnTo } from './returnTo'

export function EmailVerificationScene() {
  const [params] = useSearchParams()
  const [token] = useState(() => params.get('token'))
  const [state, setState] = useState<'checking' | 'verified' | 'failed'>(
    'checking',
  )
  const [error, setError] = useState('')
  const started = useRef(false)
  const navigate = useNavigate()
  const { refreshSession, selectProvider } = usePortalAuth()

  useEffect(() => {
    if (started.current) return
    started.current = true
    // Keep the verification credential out of browser history and later referrers.
    navigate('/verify-email', { replace: true })
    if (!isBetterAuthEnabled() || !token) {
      setError(
        'This verification link is missing or unavailable. Log in to request another email.',
      )
      setState('failed')
      return
    }
    const verify = async () => {
      try {
        const result = await getAuthClient().verifyEmail({ query: { token } })
        if (result.error) {
          setError(
            'This verification link is invalid or expired. Log in to request another email.',
          )
          setState('failed')
          return
        }
        selectProvider('better-auth')
        // Verification succeeded even if restoring the session is unavailable.
        if (await refreshSession().catch(() => false)) {
          const destination = readReturnTo()
          clearReturnTo()
          navigate(destination, { replace: true })
        } else {
          setState('verified')
        }
      } catch {
        setError(
          'We could not verify your email. Please try the link again or log in to request another email.',
        )
        setState('failed')
      }
    }
    void verify()
  }, [navigate, refreshSession, selectProvider, token])

  return (
    <LoginLayout title="Verify your email">
      {state === 'checking' && <p role="status">Verifying your email…</p>}
      {state === 'failed' && <p role="alert">{error}</p>}
      {state === 'verified' && (
        <p role="status">
          Your email is verified. Log in to continue to Portal.
        </p>
      )}
      {state !== 'checking' && (
        <Link className="underline" to="/login">
          Go to login
        </Link>
      )}
    </LoginLayout>
  )
}
